/**
 * HaymakerSkillEffect — Grit hurtmore-path charged strike.
 *
 * Hold to charge a crystalline spike that extends from the player's fist.
 * Tap for 1.5× Shatterpoint damage. Full charge at 3 seconds hits for
 * 4.5× with guaranteed knockback and stagger. Player cannot move or cancel
 * during the charge window. Releasing fires the spike with damage scaling
 * linearly between the two extremes.
 *
 * Uses a custom state machine (idle → charging → recovery → cooldown)
 * rather than PhasedSkillEffect, because the charge phase has variable
 * duration determined by hold time — not a fixed timer.
 *
 * The ChargeSystem shared primitive is NOT used here — it handles
 * accumulation between activations, not within-activation hold time.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see grit-spec.md § Haymaker
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface HaymakerDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly statusEffects: {
    apply(statusId: string, ...args: unknown[]): void;
    has(statusId: string, ...args: unknown[]): boolean;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface HaymakerConfig {
  readonly maxChargeMs: number;
  readonly recoveryMs: number;
  readonly cooldownMs: number;
  readonly baseDamage: number;
  readonly minDamageMultiplier: number;
  readonly maxDamageMultiplier: number;
  readonly staggerDurationMs: number;
  readonly chargeFieldRangeTiles: number;
}

const DEFAULT_CONFIG: HaymakerConfig = {
  maxChargeMs: 3000,
  recoveryMs: 300,
  cooldownMs: 2000,           // "long" cooldown tier
  baseDamage: 49,             // Shatterpoint base damage
  minDamageMultiplier: 1.5,   // Tap damage multiplier
  maxDamageMultiplier: 4.5,   // Full charge damage multiplier
  staggerDurationMs: 1000,
  chargeFieldRangeTiles: 1,   // Full Charge passive: proximity knockback radius
};

// ---------------------------------------------------------------------------
// Phase type
// ---------------------------------------------------------------------------

export type HaymakerPhase = 'idle' | 'charging' | 'recovery' | 'cooldown';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class HaymakerSkillEffect implements SkillEffect {
  readonly skillId = 'haymaker';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: HaymakerDeps;
  private readonly config: HaymakerConfig;

  private _phase: HaymakerPhase = 'idle';
  private _phaseTimer = 0;
  private _heldMs = 0;
  private _complete = false;

  private _resolvedDamage = 0;
  private _chargeRatio = 0;
  private _knockbackApplied = false;
  private _ignoreKnockbackResistance = false;
  private _staggerApplied = false;
  private _movementLocked = false;
  private _hitApplied = false;
  private _chargeFieldActive = false;
  private _chargeFieldRangeTiles = 0;

  constructor(deps: HaymakerDeps, config: Partial<HaymakerConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get phase(): HaymakerPhase { return this._phase; }
  get heldMs(): number { return this._heldMs; }
  get chargeRatio(): number { return this._chargeRatio; }
  get resolvedDamage(): number { return this._resolvedDamage; }
  get knockbackApplied(): boolean { return this._knockbackApplied; }
  get ignoreKnockbackResistance(): boolean { return this._ignoreKnockbackResistance; }
  get staggerApplied(): boolean { return this._staggerApplied; }
  get movementLocked(): boolean { return this._movementLocked; }
  get hitApplied(): boolean { return this._hitApplied; }
  get chargeFieldActive(): boolean { return this._chargeFieldActive; }
  get chargeFieldRangeTiles(): number { return this._chargeFieldRangeTiles; }

  get cooldown(): number {
    return this._phase === 'cooldown' ? this._phaseTimer : 0;
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._phase === 'idle';
  }

  activate(): boolean {
    if (this._phase !== 'idle') return false;

    this._phase = 'charging';
    this._heldMs = 0;
    this._complete = false;
    this._resolvedDamage = 0;
    this._chargeRatio = 0;
    this._knockbackApplied = false;
    this._ignoreKnockbackResistance = false;
    this._staggerApplied = false;
    this._hitApplied = false;
    this._movementLocked = true;

    // Full Charge passive: proximity knockback field during charging
    const hasField = this.deps.resolver.hasEffect('haymaker', 'chargeFieldKnockback');
    this._chargeFieldActive = hasField;
    this._chargeFieldRangeTiles = hasField ? this.config.chargeFieldRangeTiles : 0;

    return true;
  }

  deactivate(): void {
    if (this._phase !== 'charging') return;
    this.resolveHit();
    this.enterRecovery();
  }

  isActive(): boolean {
    return this._phase === 'charging' || this._phase === 'recovery';
  }

  isComplete(): boolean {
    return this._complete;
  }

  interrupt(): void {
    if (this._phase === 'idle' || this._phase === 'cooldown') return;
    this._phase = 'idle';
    this._phaseTimer = 0;
    this._movementLocked = false;
    this._chargeFieldActive = false;
    this._chargeFieldRangeTiles = 0;
    this._complete = true;
  }

  update(delta: number): void {
    if (this._phase === 'idle') return;

    if (this._phase === 'charging') {
      this._heldMs += delta;
      if (this._heldMs >= this.config.maxChargeMs) {
        this._heldMs = this.config.maxChargeMs;
        this.resolveHit();
        this.enterRecovery();
      }
      return;
    }

    // Recovery and cooldown: countdown timers
    this._phaseTimer -= delta;
    if (this._phaseTimer <= 0) {
      if (this._phase === 'recovery') {
        this._phase = 'cooldown';
        this._phaseTimer = this.config.cooldownMs;
        this._complete = true;
      } else if (this._phase === 'cooldown') {
        this._phase = 'idle';
        this._phaseTimer = 0;
      }
    }
  }

  destroy(): void {
    this._phase = 'idle';
    this._phaseTimer = 0;
    this._movementLocked = false;
    this._chargeFieldActive = false;
    this._chargeFieldRangeTiles = 0;
  }

  // --- Internal ---

  private resolveHit(): void {
    this._chargeRatio = Math.min(this._heldMs / this.config.maxChargeMs, 1.0);

    // Base charge-scaled damage: lerp(minMultiplier, maxMultiplier, chargeRatio)
    const chargeMultiplier = this.config.minDamageMultiplier +
      (this.config.maxDamageMultiplier - this.config.minDamageMultiplier) * this._chargeRatio;
    let damage = this.config.baseDamage * chargeMultiplier;

    // Overhand: bonus damage against staggered/stunned targets
    const staggerBonus = this.deps.resolver.getAdditiveEffect('haymaker', 'staggerBonusDamage');
    if (staggerBonus > 0 && this.deps.statusEffects.has('stagger')) {
      damage *= (1 + staggerBonus);
    }

    this._resolvedDamage = damage;

    // Full charge: knockback + stagger
    if (this._chargeRatio >= 1.0) {
      this._knockbackApplied = true;
      this._ignoreKnockbackResistance = this.deps.resolver.hasEffect(
        'haymaker', 'ignoreKnockbackResistance',
      );

      this.deps.statusEffects.apply('stagger', this.config.staggerDurationMs, 'haymaker');
      this._staggerApplied = true;
    }

    this._hitApplied = true;
  }

  private enterRecovery(): void {
    this._phase = 'recovery';
    this._phaseTimer = this.config.recoveryMs;
    this._movementLocked = false;
    this._chargeFieldActive = false;
    this._chargeFieldRangeTiles = 0;
  }
}
